import config from '@/common/config'
import store from '@state/store'
import { isHttpStatus } from '@/helpers/http-status-helpers'
import axios from 'axios'
import { omitBy } from 'lodash/object'
import { isNil } from 'lodash'
import AxiosRequestConfig from '@/models/api/axiosRequestConfig'
import HttpHeaders from '@/constants/api/HttpHeaders'
import HttpRequestUrl from '@/models/api/httpRequestUrl'
import HttpRequestConfig from '@/models/api/httpRequestConfig'
import HttpResponseType from '@/constants/api/HttpResponseType'
import { buildURL, buildAPIBasePath } from '@/helpers/url-helpers'

class BaseApiService {
  /**
   * Axios client
   */
  client

  /**
   * HTTP methods
   */
  method = {
    GET: 'get',
    POST: 'post',
    DELETE: 'delete',
    PATCH: 'patch',
    PUT: 'put',
  }

  /**
   * A particular resource, e.i. users, posts, comments etc.
   */
  resource

  /**
   * The requested API's base url
   */
  baseURL

  constructor(resource, baseURL = null) {
    if (!resource) throw new Error('Resource is not provided')
    this.resource = resource

    this.baseURL = baseURL || config.get('apiUrl')

    this.client = axios.create({
      baseURL: this.baseURL,
      json: true,
    })
  }

  /**
   * Generates a URL for the requested endpoint
   * @param {HttpRequestUrl} options
   * @returns {URL}
   */
  getUrl(options) {
    const apiUrl = buildAPIBasePath(
      options.baseUrl || this.baseURL,
      this.resource,
      options.subPath
    )

    return buildURL(apiUrl, options.query)
  }

  async handleErrors(err) {
    // If unauthorised, renew access token then retry
    const status = err?.response?.status
    if (isHttpStatus(status, 'Unauthorized')) {
      await store.dispatch('auth/getAccessTokenOrRefresh', true)

      return {
        retry: true,
        err,
      }
    }

    throw err
  }

  /**
   * Will attempt to get an auth token or refresh one then generate an auth header object
   * @returns
   */
  async generateAuthHeader() {
    const accessToken = await store.dispatch('auth/getAccessTokenOrRefresh')

    if (typeof accessToken === 'undefined' || !accessToken)
      throw Error('An access token is required for authenticated requests')

    return {
      [HttpHeaders.authorization]: `Bearer ${accessToken}`,
    }
  }

  /**
   * Generates a dictionary object of request headers
   * @param {HttpRequestConfig} options
   */
  async compileHeaders(options) {
    let authorizationHeader = {}

    if (options.isAuthenticated)
      authorizationHeader = await this.generateAuthHeader()

    const headers = {
      ...options.headers,
      ...authorizationHeader,
    }

    return headers
  }

  /**
   * Compiles request configuration and handles tasks like generating headers
   * list and retrieving the auth token
   * @param {HttpRequestConfig} options
   * @returns {Promise<AxiosRequestConfig>} Request config object
   */
  async compileRequestConfig(options) {
    const headers = await this.compileHeaders(options)

    return new AxiosRequestConfig({
      method: options.method,
      url: this.getUrl(options.url),
      headers,
      responseType: options.responseType,
      data: options.data,
    })
  }

  /**
   * Generates httpClient config and calls the httpClient with that config
   * @param {HttpRequestConfig} options
   */
  async makeHttpRequest(options) {
    const config = await this.compileRequestConfig(options)

    return await this.client(config)
  }

  /**
   * Executes a HTTP request
   * @param {HttpRequestConfig} options
   * @param {Boolean} retry Indicates to recursive call if in retry step to prevent infinite retry loop
   * @returns {Promise<ResultDTO>}
   */
  async execute(options, retry = false) {
    try {
      /**
       * @type {data: Object, status: Number, headers: Object}
       */
      const response = await this.makeHttpRequest(options)

      return {
        data: response.data,
        status: response.status,
        headers: response.headers,
      }
    } catch (ex) {
      const response = await this.handleErrors(ex)

      // Only retry request if haven't already retried request before
      if (response.retry && !retry) return await this.execute(options, true)

      throw response.err
    }
  }

  /**
   * Executes an authenticated HTTP request for blob files.
   * @param {Object} params Parameters for the request.
   * @param {String} params.method HTTP Method (GET, POST, PATCH, DELETE, PUT).
   * @param {String} params.url Endpoint URL.
   * @param {*} params.data Payload to send to the server.
   * @param {*} params.providedHeaders Request headers to send to the server.
   * @returns {Promise<*>} HTTP response.
   */
  async executeBlob({ method, url, data, providedHeaders }) {
    return this.execute(
      new HttpRequestConfig({
        method,
        url,
        data,
        headers: providedHeaders,
        responseType: HttpResponseType.blob,
      })
    )
  }

  /**
   * Executes an unauthenticated HTTP request.
   * @param {Object} params Parameters for the request.
   * @param {String} params.method HTTP Method (GET, POST, PATCH, DELETE, PUT).
   * @param {String} params.url Endpoint URL.
   * @param {*} params.data Payload to send to the server.
   * @returns {Promise<*>} HTTP response.
   */
  async executeAnon({ method, url, data }) {
    return this.client({
      method,
      url,
      data,
    })
  }

  /**
   * Mapper that accepts a 1D object and generates a query string to be appended to a URL
   * @param {Object} queryParams key value object { key: value, ... }
   * @returns A query string e.g. ?key=value&key2=value2...
   */
  mapQueryParams(queryParams) {
    if (!queryParams) return ''

    const notNullParams = omitBy(queryParams, (val) => isNil(val))
    const urlSearchParams = new URLSearchParams()

    // We are ensuring to add separate entries for arrays as this is how query params work e.g [1,2,3] => list=1&list=2&list=3
    Object.entries(notNullParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((value) => urlSearchParams.append(key, value.toString()))
      } else {
        urlSearchParams.append(key, value.toString())
      }
    })

    return '?' + urlSearchParams.toString()
  }
}

class ReadOnlyApiService extends BaseApiService {
  /**
   * Generic configurable authenticated HTTP request
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @returns Http response
   */
  async fetch(method, url, data, headers) {
    return this.execute(new HttpRequestConfig({ method, url, data, headers }))
  }

  /**
   * HTTP Get authenticated request
   * @param {*} args
   * @param {*} query query object {key: value}
   * @returns
   */
  async get(subPath, query = null) {
    return this.execute(
      new HttpRequestConfig({
        method: this.method.GET,
        url: new HttpRequestUrl({ subPath, query }),
      })
    )
  }

  /**
   * Generic configurable unauthenticated HTTP request
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @returns Http response
   */
  async fetchAnon(method, url, data, headers) {
    return this.executeAnon({
      method,
      url,
      data,
      headers,
      isAuthenticated: false,
    })
  }

  /**
   * HTTP Get unauthenticated request
   * @param {*} args
   * @param {*} query query object {key: value}
   * @returns
   */
  async getAnon(subPath, query = null) {
    return this.executeAnon({
      method: this.method.GET,
      url: new HttpRequestUrl({ subPath, query }),
      isAuthenticated: false,
    })
  }

  /**
   * Generic configurable Authenticated HTTP request - Response type: Blob
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @returns Http response
   */
  async fetchBlob(method, url, data, headers) {
    return this.executeBlob({ method, url, data, headers })
  }

  /**
   * HTTP Get authenticated request - Response type: Blob
   * @param {*} args
   * @param {*} query query object {key: value}
   * @returns
   */
  async getBlob(subPath, query = null) {
    return this.executeBlob({
      method: this.method.GET,
      url: new HttpRequestUrl({ subPath, query }),
    })
  }
}

class ModelApiService extends ReadOnlyApiService {
  /**
   * HTTP Post authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async post(subPath, data = {}, query = null) {
    return this.execute(
      new HttpRequestConfig({
        method: this.method.POST,
        url: new HttpRequestUrl({ subPath, query }),
        data,
      })
    )
  }

  /**
   * HTTP Post unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async postAnon(subPath, data = {}, query = null) {
    return this.executeAnon({
      method: this.method.POST,
      url: new HttpRequestUrl({ subPath, query }),
      data,
      isAuthenticated: false,
    })
  }

  /**
   * HTTP Post authenticated request - Response type: Blob
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async postBlob(subPath, data = {}, query = null) {
    return this.executeBlob({
      method: this.method.POST,
      url: new HttpRequestUrl({ subPath, query }),
      data,
    })
  }

  /**
   * HTTP Put authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async put(subPath, data = {}, query = null) {
    return this.execute(
      new HttpRequestConfig({
        method: this.method.PUT,
        url: new HttpRequestUrl({ subPath, query }),
        data,
      })
    )
  }

  /**
   * HTTP Put unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async putAnon(subPath, data = {}, query = null) {
    return this.executeAnon({
      method: this.method.PUT,
      url: new HttpRequestUrl({ subPath, query }),
      data,
      isAuthenticated: false,
    })
  }

  /**
   * HTTP Put authenticated request - Response type: Blob
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async putBlob(subPath, data = {}, query = null) {
    return this.executeBlob({
      method: this.method.PUT,
      url: new HttpRequestUrl({ subPath, query }),
      data,
      isBlob: true,
    })
  }

  /**
   * HTTP Patch authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async patch(subPath, data = {}, query = null) {
    return this.execute(
      new HttpRequestConfig({
        method: this.method.PATCH,
        url: new HttpRequestUrl({ subPath, query }),
        data,
      })
    )
  }

  /**
   * HTTP Patch unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async patchAnon(subPath, data = {}, query = null) {
    return this.executeAnon({
      method: this.method.PATCH,
      url: new HttpRequestUrl({ subPath, query }),
      data,
      isAuthenticated: false,
    })
  }

  /**
   * HTTP Delete authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async delete(subPath, data = {}, query = null) {
    return this.execute(
      new HttpRequestConfig({
        method: this.method.DELETE,
        url: new HttpRequestUrl({ subPath, query }),
        data,
      })
    )
  }

  /**
   * HTTP Delete unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async deleteAnon(subPath, data = {}, query = null) {
    return this.executeAnon({
      method: this.method.DELETE,
      url: new HttpRequestUrl({ subPath, query }),
      data,
      isAuthenticated: false,
    })
  }
}

export { ReadOnlyApiService, ModelApiService }
